import {
  trackMetaPixelCustomEvent,
  trackMetaPixelStandardEvent,
} from '@/lib/ads/metaPixel';
import { logInfo } from '@/lib/logger';
import { identifyAndTrackMixpanelEvent, trackMixpanelEvent } from '@/lib/mixpanel';

type ChallengeLandingViewedFields = {
  challengePublicId: string;
  landingTouchId: string;
  challengeShareId: string | null;
  deduped: boolean;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmId: string | null;
};

type ChallengeCtaClickedFields = {
  challengePublicId: string;
  landingTouchId: string | null;
  destination: 'sign_in' | 'sign_up' | 'play';
};

type AuthCompletedFields = {
  anonId: string;
  challengePublicId: string;
  landingTouchId: string;
  challengeShareId: string | null;
  claimStage: string;
  isFirstTouchForUser: boolean;
  userId: string;
};

type ChallengeShareSheetOpenedFields = {
  sessionId: string;
  attemptId: string;
  attemptNo: number;
  challengePublicId: string;
};

type ChallengeShareInitiatedFields = {
  sessionId: string;
  attemptId: string;
  attemptNo: number;
  challengePublicId: string;
  method: 'native_share' | 'download' | 'copy_link';
  shareTarget: string | null;
  backendRecorded: boolean;
};

const buildMetaCompleteRegistrationEventId = (
  landingTouchId: string,
  userId: string
): string => {
  const eventId = `meta.complete_registration:${landingTouchId}:${userId}`;
  return eventId;
};

export const trackChallengeLandingViewed = (fields: ChallengeLandingViewedFields): void => {
  const eventFields = {
    challenge_public_id: fields.challengePublicId,
    landing_touch_id: fields.landingTouchId,
    challenge_share_id: fields.challengeShareId,
    deduped: fields.deduped,
    utm_source: fields.utmSource,
    utm_medium: fields.utmMedium,
    utm_campaign: fields.utmCampaign,
    utm_id: fields.utmId,
  };

  logInfo('challenge.landing_viewed', 'Challenge landing viewed', eventFields);
  trackMixpanelEvent('challenge.landing_viewed', eventFields);
  trackMetaPixelStandardEvent('ViewContent', {
    challenge_public_id: fields.challengePublicId,
    challenge_share_id: fields.challengeShareId,
    content_category: 'challenge',
    content_ids: [fields.challengePublicId],
    content_name: 'challenge_landing',
    deduped: fields.deduped,
    landing_touch_id: fields.landingTouchId,
    utm_campaign: fields.utmCampaign,
    utm_id: fields.utmId,
    utm_medium: fields.utmMedium,
    utm_source: fields.utmSource,
  });
};

export const trackChallengeCtaClicked = (fields: ChallengeCtaClickedFields): void => {
  const eventFields = {
    challenge_public_id: fields.challengePublicId,
    landing_touch_id: fields.landingTouchId,
    destination: fields.destination,
  };

  logInfo('challenge.cta_clicked', 'Challenge CTA clicked', eventFields);
  trackMixpanelEvent('challenge.cta_clicked', eventFields);
  trackMetaPixelCustomEvent('ChallengeCtaClicked', eventFields);
};

export const trackAuthCompleted = (fields: AuthCompletedFields): void => {
  const eventFields = {
    anon_id: fields.anonId,
    challenge_public_id: fields.challengePublicId,
    landing_touch_id: fields.landingTouchId,
    challenge_share_id: fields.challengeShareId,
    claim_stage: fields.claimStage,
    is_first_touch_for_user: fields.isFirstTouchForUser,
    user_id: fields.userId,
  };

  logInfo('auth.completed', 'Challenge auth completed', eventFields);
  identifyAndTrackMixpanelEvent(fields.userId, 'auth.completed', eventFields);
  const metaEventId = buildMetaCompleteRegistrationEventId(
    fields.landingTouchId,
    fields.userId
  );
  trackMetaPixelStandardEvent(
    'CompleteRegistration',
    {
      challenge_public_id: fields.challengePublicId,
      challenge_share_id: fields.challengeShareId,
      content_category: 'challenge',
      content_ids: [fields.challengePublicId],
      content_name: 'challenge_auth_claim',
      is_first_touch_for_user: fields.isFirstTouchForUser,
      landing_touch_id: fields.landingTouchId,
      status: fields.claimStage,
    },
    {
      eventId: metaEventId,
    }
  );
};

export const trackChallengeShareSheetOpened = (
  fields: ChallengeShareSheetOpenedFields
): void => {
  const eventFields = {
    session_id: fields.sessionId,
    attempt_id: fields.attemptId,
    attempt_no: fields.attemptNo,
    challenge_public_id: fields.challengePublicId,
  };

  logInfo('challenge.share_sheet_opened', 'Challenge share sheet prepared', eventFields);
  trackMixpanelEvent('challenge.share_sheet_opened', eventFields);
};

export const trackChallengeShareInitiated = (
  fields: ChallengeShareInitiatedFields
): void => {
  const eventFields = {
    session_id: fields.sessionId,
    attempt_id: fields.attemptId,
    attempt_no: fields.attemptNo,
    challenge_public_id: fields.challengePublicId,
    method: fields.method,
    share_target: fields.shareTarget,
    backend_recorded: fields.backendRecorded,
  };

  logInfo('challenge.share_initiated', 'Challenge share initiated', eventFields);
  trackMixpanelEvent('challenge.share_initiated', eventFields);
};
